import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Globe, Images, ChevronDown, CheckCircle } from "lucide-react";
import { cn } from "../../lib/utils";
import countries from "./countries";
import { CardFooter } from '../../components/ui/card';
import { SelectGroup } from '../../components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "../../components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover"
import { Button } from "../../components/ui/button"
import { Check, ChevronsUpDown } from "lucide-react"
import { useTranslation } from '../../i18n/hooks';

type ImageFinderFeatureProps = {
    isVisible: boolean;
    isSelected: boolean;
    onVisibilityChange: (visible: boolean) => void;
    onSelectionChange: (selected: boolean) => void;
    geo: string;
    onGeoChange: (geo: string) => void;
  };
  
  const CountryCombobox = ({ value, onChange }: { value: string, onChange: (value: string) => void }) => {
    const [open, setOpen] = useState(false)
    const { t } = useTranslation();
  
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-[200px] justify-between"
          >
            {value
              ? countries.find((country) => country.value === value)?.label
              : t("enrichmentConfig.select_country")}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0">
          <Command 

            filter={(value, search) => {
              const found = countries.find((country) => country.label.toLowerCase().includes(search.toLowerCase()))
              if(found?.value === value) {
                return 1
              } else {
                return 0
              }
            }}
            
          >
            <CommandInput placeholder={t("enrichmentConfig.search_country")} />
            <CommandEmpty>{t("enrichmentConfig.no_country_found")}</CommandEmpty>
            <CommandList>
            <CommandGroup>
              {countries.map((country) => (
                <CommandItem
                  key={country.value}
                  // This remains as value for selection purposes
                  value={country.value}
                  onSelect={(currentValue) => {
                    onChange(currentValue === value ? "" : currentValue)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === country.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {country.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }

  export const ImageFinderFeature = ({
    isVisible,
    isSelected,
    onVisibilityChange,
    onSelectionChange,
    geo,
    onGeoChange
  }: ImageFinderFeatureProps) => {
    const { t } = useTranslation();
    
    return (
      <Card
        className={cn(
          "transition-all",
          isSelected
            ? "bg-gradient-to-r from-blue-50 to-blue-100 shadow-lg"
            : "hover:bg-gray-50/50"
        )}
      >
        <CardHeader
          className="cursor-pointer"
          onClick={() => onVisibilityChange(!isVisible)}
        >
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Images className="w-5 h-5 text-blue-600" />
            </div>

            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <CardTitle>
                      {t("enrichmentConfig.feature_image_finder")}
                    </CardTitle>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                      {t("enrichmentConfig.new_tag")}
                    </span>
                  </div>
                  <CardDescription className="mt-1">
                    {t("enrichmentConfig.image_finder_description_long")}
                  </CardDescription>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">
                      {t("enrichmentConfig.image_credits_per_product")}
                    </span>
                  </div>
                  <ChevronDown
                    className={cn(
                      "w-5 h-5 text-gray-500 transition-transform",
                      isVisible && "transform rotate-180"
                    )}
                  />
                </div>
              </div>

              {/* Stats Section */}
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-600">
                    {t("enrichmentConfig.high_quality_images")}
                  </span>
                </div>
                <div className="w-1 h-1 bg-gray-300 rounded-full" />
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-600">
                    {t("enrichmentConfig.multiple_angles")}
                  </span>
                </div>
                {!isVisible && (
                  <>
                    <div className="w-1 h-1 bg-gray-300 rounded-full" />
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-600">
                        {t("enrichmentConfig.search_location")}:{" "}
                        {countries.find((c) => c.value === geo)?.label || geo}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardHeader>

        {isVisible && (
          <CardContent className="space-y-6">
            {/* Market Region Selection */}
            <div className="p-4 bg-white rounded-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-50 rounded-full">
                  <Globe className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium">
                    {t("enrichmentConfig.market_region")}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {t("enrichmentConfig.select_region")}
                  </p>
                </div>
              </div>
              <CountryCombobox value={geo} onChange={onGeoChange} />
            </div>

            {/* Feature Description */}
            <div className="p-4 bg-white bg-opacity-50 rounded-lg space-y-4">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900">
                    {t("enrichmentConfig.what_youll_get")}:
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {t("enrichmentConfig.image_benefit_1")}
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {t("enrichmentConfig.image_benefit_2")}
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {t("enrichmentConfig.image_benefit_3")}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div style={{
              position: "relative",
              overflow: "hidden",
              aspectRatio: "1920/1080"
            }}>
              <iframe
                src="https://share.synthesia.io/embeds/videos/8aa88116-8b81-46d2-8326-3d47b8917dd5"
                loading="lazy"
                title="Synthesia video player - Marketplace IQ Image Finder"
                allowFullScreen
                allow="encrypted-media; fullscreen;"
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  top: 0,
                  left: 0,
                  border: "none",
                  padding: 0,
                  margin: 0,
                  overflow: "hidden"
                }}
              ></iframe>
            </div>

          </CardContent>
        )}
      </Card>
    );
  };

export default ImageFinderFeature;